import React, { FC, useEffect, useRef, useState } from 'react';
import { ReactComponent as HeadphonesIcon } from 'bootstrap-icons/icons/headphones.svg';
import { ReactComponent as MicFillIcon } from 'bootstrap-icons/icons/mic-fill.svg';
import { QuizQuestion } from '../../shared/types';
import { GoogleGenAI } from '@google/genai';
import { blobToBase64, compareText } from '../../shared/utils';

type Props = {
  data: QuizQuestion;
  isSubmitted: boolean;
  currentAnswer: string;
  setCurrentAnswer: (val: string) => void;
  pronounceAudio?: (isQuestionPlay?: boolean) => void;
  handleSubmit: (correct?: boolean) => void;
};

const GEMINI_API_KEY = process.env.REACT_APP_API_KEY;
const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

export const CardTemplate11: FC<Props> = ({ data, isSubmitted, setCurrentAnswer, pronounceAudio, handleSubmit }) => {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState('');

  async function start() {
    setTranscript('');
    chunksRef.current = [];

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
    recorder.start();

    setListening(true);
  }

  async function stop() {
    if (!listening) {
      setTranscript('');
      setCurrentAnswer('');
      return;
    }
    recorderRef.current?.stop();
    await new Promise((r) => (recorderRef.current!.onstop = r));

    streamRef.current?.getTracks().forEach((t) => t.stop());

    const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
    const audioBase64 = await blobToBase64(audioBlob);

    setLoading(true);
    let result;
    try {
      result = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: [
          {
            role: 'user',
            parts: [
              {
                inlineData: {
                  mimeType: 'audio/webm',
                  data: audioBase64,
                },
              },
              {
                text: `
                    Распознай речь из аудио.
                    Язык речи — казахский (kk).
                    Распознай отчетливо и внимательно!!!
                    Верни только распознанный текст.
                    Без комментариев, без перевода.
                    `.trim(),
              },
            ],
          },
        ],
      });
    } catch (err) {
      console.log(err);
    }
    setLoading(false);

    setTranscript(result?.text ?? '');
    setCurrentAnswer(result?.text ?? '');
    setListening(false);
    return compareText(result?.text ?? '', data?.qs_dsc);
  }

  // const handleMicClick = () => {
  //   if (isSubmitted) return;
  //   if (listening) {
  //     SpeechRecognition.stopListening().then(() => {
  //       setCurrentAnswer(transcript ?? '');
  //     });
  //   } else {
  //     SpeechRecognition.startListening({ language: 'kk-KZ', interimResults: false });
  //   }
  // };

  const handlePronounceAudio = () => {
    if (pronounceAudio) {
      pronounceAudio(true);
    }
  };

  const onSubmit = async () => {
    const correct = await stop();
    handleSubmit(correct);
  };

  useEffect(() => {
    handlePronounceAudio();
  }, [data.id]);

  return (
    <>
      <div className="text-center">
        <h4 className="h3 tw-underline">{data.qs_main}</h4>
        <div className="my-4 tw-flex tw-justify-center tw-items-center tw-flex-wrap gap-2">
          {!!pronounceAudio && (
            <HeadphonesIcon
              className={`tw-text-[#0c276c] tw-w-[36px] tw-cursor-pointer hover:tw-opacity-80 ${!isSubmitted ? 'hover:tw-text-orange-500 tw-transition-all' : ''}`}
              width={36}
              height={36}
              onClick={handlePronounceAudio}
            ></HeadphonesIcon>
          )}
          <p className="my-sm-5 tw-text-xl sm:tw-text-2xl mb-0">{data.qs_dsc}</p>
        </div>
        {/*{browserSupportsSpeechRecognition ? (*/}
        {/*  <div>*/}
        {/*    <i*/}
        {/*      className={`bi bi-mic-fill tw-text-5xl tw-cursor-pointer hover:tw-opacity-80 ${transcript ? 'tw-text-green-500' : 'tw-text-[#0c276c]'} ${listening ? 'tw-animate-pulse !tw-text-red-700' : ''}`}*/}
        {/*      onClick={handleMicClick}*/}
        {/*    ></i>*/}
        {/*    <p className="mt-2 mb-4 mb-sm-5 tw-text-lg sm:tw-text-xl">(Микрофонға қайталап айтыңыз)</p>*/}
        {/*  </div>*/}
        {/*) : (*/}
        {/*  <p className="mt-1 mb-4 mb-sm-5 tw-text-lg sm:tw-text-xl">Бұл браузерде сөйлеуді тану қолжетімсіз</p>*/}
        {/*)}*/}
        <div>
          <MicFillIcon
            className={`${!isSubmitted ? 'tw-cursor-pointer hover:tw-opacity-80' : ''} ${transcript || loading ? 'tw-text-green-500' : 'tw-text-[#0c276c]'} ${listening ? 'tw-animate-pulse !tw-text-red-700' : ''}`}
            width={48}
            height={48}
            onClick={loading || transcript || listening ? undefined : start}
          ></MicFillIcon>
          <p className="mt-2 mb-4 mb-sm-5 tw-text-lg sm:tw-text-xl">(Микрофонға қайталап айтыңыз)</p>
        </div>
      </div>
      {!isSubmitted && (
        <button
          className="btn btn-primary mt-3 px-4 tw-font-bold tw-w-full min-[400px]:tw-w-[260px] sm:!tw-text-xl"
          disabled={loading}
          onClick={onSubmit}
        >
          {loading ? 'Жазба өңделуде...' : 'ТЕКСЕРУ'}
        </button>
      )}
    </>
  );
};
